import React from 'react';
import JsonLd from './JsonLd';
import { Link } from 'react-router-dom';
import { Quote, ArrowRight } from 'lucide-react';

/**
 * Boeing Technical Fellow standalone testimonial card.
 * Glass / glassmorphism treatment — soft blue gradient background,
 * subtle backdrop blur, aviation-blue accent line, oversized pull-quote.
 *
 * Renders a schema.org/Review JSON-LD block so LLMs and search engines
 * can attribute the quote to a Boeing Technical Fellow as a first-party
 * testimonial for the FOTRIC acoustic imager product line.
 */
const BoeingFellowTestimonial = ({ onRequestQuote }) => {
  const reviewJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Review',
    itemReviewed: {
      '@type': 'Product',
      name: 'FOTRIC Acoustic Imaging Cameras',
      brand: { '@type': 'Brand', name: 'FOTRIC' },
      url: 'https://ctrl.aero/products/fotric',
    },
    reviewBody:
      '(FOTRIC H6) outperformed all of them (Fluke ii905 and TPI 9050). I love this thing. The form factor is amazing, but the picture/resolution is incredible. I don\'t think I can go back to the other devices.',
    author: {
      '@type': 'Person',
      name: 'Boeing Technical Fellow',
      jobTitle: 'Technical Fellow',
      worksFor: { '@type': 'Organization', name: 'The Boeing Company' },
    },
    reviewRating: {
      '@type': 'Rating',
      ratingValue: '5',
      bestRating: '5',
    },
  };

  return (
    <section
      id="boeing-fellow-testimonial"
      className="relative py-16 sm:py-20 bg-gradient-to-br from-[#0b1a2b] via-[#0f2540] to-[#0b1a2b] overflow-hidden"
      data-testid="boeing-fellow-testimonial"
    >
      <JsonLd data={reviewJsonLd} />

      {/* Ambient blue orbs for depth */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 -left-24 w-[420px] h-[420px] rounded-full bg-blue-500/20 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-24 -right-24 w-[380px] h-[380px] rounded-full bg-orange-500/10 blur-3xl"
      />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section eyebrow */}
        <div className="flex items-center gap-3 mb-6">
          <div className="h-px w-10 bg-blue-400/60" />
          <h3 className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-blue-300">
            Field Validation
          </h3>
        </div>

        {/* Glass card */}
        <figure
          className="relative rounded-2xl border border-white/15 bg-white/[0.06] backdrop-blur-xl shadow-[0_20px_60px_-20px_rgba(0,0,0,0.5)] overflow-hidden"
          data-testid="boeing-fellow-card"
        >
          {/* Accent bar */}
          <div
            aria-hidden="true"
            className="absolute top-0 left-0 h-full w-1 bg-gradient-to-b from-blue-400 via-blue-500 to-orange-500"
          />

          <div className="p-8 sm:p-12 lg:p-16">
            <Quote
              aria-hidden="true"
              className="w-12 h-12 sm:w-16 sm:h-16 text-blue-300/40 mb-6"
              strokeWidth={1.25}
            />

            <blockquote>
              <p
                className="text-2xl sm:text-3xl lg:text-4xl leading-snug font-medium text-white tracking-tight"
                data-testid="boeing-fellow-quote"
              >
                <span className="text-blue-300">(FOTRIC H6)</span> outperformed all of them
                (Fluke ii905 and TPI 9050).{' '}
                <span className="text-white/80">
                  I love this thing. The form factor is amazing, but the picture/resolution
                  is incredible.
                </span>{' '}
                I don&rsquo;t think I can go back to the other devices.
              </p>
            </blockquote>

            <figcaption className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
              <div>
                <div className="text-sm font-semibold tracking-wide uppercase text-blue-300">
                  Verbatim Testimonial
                </div>
                <cite className="not-italic block mt-1 text-lg sm:text-xl font-semibold text-white">
                  Boeing Technical Fellow
                </cite>
                <div className="text-sm text-blue-100/70">
                  The Boeing Company &middot; MRO Ultrasound &amp; Acoustic Imaging
                </div>
              </div>

              {/* Price framing chip */}
              <div
                className="rounded-xl border border-white/15 bg-white/[0.04] px-5 py-4 sm:min-w-[260px]"
                data-testid="boeing-fellow-price-chip"
              >
                <h3 className="text-[11px] font-semibold tracking-[0.18em] uppercase text-blue-300 mb-1">
                  Price Advantage
                </h3>
                <div className="text-white text-base sm:text-lg font-semibold leading-tight">
                  $5,000 – $12,000
                </div>
                <div className="text-blue-100/70 text-sm leading-snug">
                  vs $20,000+ competitors &mdash; 40 to 75% below market
                </div>
              </div>
            </figcaption>
          </div>

          {/* Bottom action bar */}
          <div className="border-t border-white/10 bg-white/[0.03] px-8 sm:px-12 lg:px-16 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <p className="text-sm text-blue-100/80">
              See the FOTRIC H6 in the{' '}
              <Link
                to="/products/acoustic-cameras/h-series"
                className="text-blue-300 hover:text-white underline underline-offset-4 transition-colors"
                data-testid="boeing-fellow-h6-link"
              >
                H-Series lineup
              </Link>{' '}
              or compare across the full range below.
            </p>
            <Link
              to="/#contact"
              onClick={(e) => {
                if (onRequestQuote) {
                  e.preventDefault();
                  onRequestQuote({ product: 'FOTRIC H6 — Boeing Fellow Testimonial' });
                }
              }}
              className="inline-flex items-center gap-2 text-sm font-semibold text-white hover:text-blue-300 transition-colors group"
              data-testid="boeing-fellow-quote-cta"
            >
              Request a Quote
              <ArrowRight
                className="w-4 h-4 transition-transform group-hover:translate-x-1"
                aria-hidden="true"
              />
            </Link>
          </div>
        </figure>
      </div>
    </section>
  );
};

export default BoeingFellowTestimonial;
